import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import type { AxisConfiguration } from 'components/molecules/PackArchitecture/AxisConfigurationModal';
import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/KpiAxisConfigurationModal';
import {
    buildAxisGroupByControlsMeta,
    buildBubbleRepresentsOptionGroups,
    buildBubbleSizeOptionGroups,
    buildKpiAxisControlsMeta,
    buildPpaChartRequest,
    buildXAxisOptionGroups,
    buildYAxisOptionGroups,
    findAxisConfig,
    getAxisLabel,
    getHeatMapToggleConfig,
    isKpiXAxisValue,
    getTableKpiOptionItems,
    getTableTooltip,
} from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import { appendTableSearchToRequest } from 'components/pages/PackArchitecture/packArchitectureChartRequest.helpers';
import type { PricePackArchitectureMetaResponse } from 'components/pages/PackArchitecture/packArchitectureMeta.types';
import { ppaUrls } from 'config/urls';
import { usePpaQueryFilters } from 'hooks/ppa/usePpaQueryFilters';

import { usePackArchitectureAxisInitialization } from './usePackArchitectureAxisInitialization';
import { usePackArchitectureTableControls } from './usePackArchitectureTableControls';

export function usePackArchitectureAxisConfig() {
    const {
        dim1_id_list,
        dim2_id_list,
        hasResolvedFilterIds,
        startTimestamp,
        endTimestamp,
        hasValidTimePeriod,
        filtersLoading,
        globalFilterQueryKey,
        filterNullError,
    } = usePpaQueryFilters();
    const [xAxis, setXAxis] = useState('');
    const [yAxis, setYAxis] = useState('');
    const [bubbleRepresents, setBubbleRepresents] = useState('');
    const [bubbleSize, setBubbleSize] = useState('');
    const [showHeatMap, setShowHeatMap] = useState(false);
    const [xAxisDimensionConfig, setXAxisDimensionConfig] = useState<AxisConfiguration | undefined>();
    const [xAxisKpiConfig, setXAxisKpiConfig] = useState<KpiAxisConfiguration | undefined>();

    const { data: meta, isLoading: isMetaLoading } = useQuery<PricePackArchitectureMetaResponse>({
        queryKey: ['price-pack-architecture-meta'],
        queryFn: async () => {
            const response = await axios.get(ppaUrls.getPricePackArchitectureMetaUrl);
            const body = response.data;
            return (body?.price_pack_architecture ?? body) as PricePackArchitectureMetaResponse;
        },
    });

    const title = meta?.title ?? '';
    const tooltip = meta?.tooltip ?? '';

    const xAxisConfig = useMemo(() => findAxisConfig(meta, 'x_axis'), [meta]);
    const yAxisConfig = useMemo(() => findAxisConfig(meta, 'y_axis'), [meta]);
    const bubbleRepresentsConfig = useMemo(() => findAxisConfig(meta, 'bubble_represents'), [meta]);
    const bubbleSizeConfig = useMemo(() => findAxisConfig(meta, 'bubble_size'), [meta]);
    const heatMapToggle = useMemo(() => getHeatMapToggleConfig(meta), [meta]);
    const tableKpiOptionItems = useMemo(() => getTableKpiOptionItems(meta), [meta]);
    const tableTooltip = useMemo(() => getTableTooltip(meta), [meta]);

    const {
        selectedKpis,
        toggleTableKpi,
        tableSearchInput,
        setTableSearchInput,
        submitTableSearch,
        tableSearchKeyword,
    } = usePackArchitectureTableControls(yAxis, tableKpiOptionItems);

    const allTableKpiIds = useMemo(
        () => tableKpiOptionItems.map((item) => item.id).filter((id): id is string => Boolean(id)),
        [tableKpiOptionItems],
    );

    const isInitialized = usePackArchitectureAxisInitialization({
        meta,
        xAxisConfig,
        yAxisConfig,
        bubbleRepresentsConfig,
        bubbleSizeConfig,
        heatMapToggle,
        setXAxis,
        setYAxis,
        setBubbleRepresents,
        setBubbleSize,
        setShowHeatMap,
    });

    const xAxisOptionGroups = useMemo(() => buildXAxisOptionGroups(xAxisConfig), [xAxisConfig]);
    const yAxisOptionGroups = useMemo(() => buildYAxisOptionGroups(yAxisConfig), [yAxisConfig]);
    const isXAxisKpi = useMemo(() => isKpiXAxisValue(xAxis, xAxisConfig), [xAxis, xAxisConfig]);
    const bubbleRepresentsOptionGroups = useMemo(
        () => buildBubbleRepresentsOptionGroups(bubbleRepresentsConfig),
        [bubbleRepresentsConfig],
    );
    const bubbleSizeOptionGroups = useMemo(() => buildBubbleSizeOptionGroups(bubbleSizeConfig), [bubbleSizeConfig]);

    const xAxisGroupByControlsMeta = useMemo(
        () => buildAxisGroupByControlsMeta(xAxisConfig, getAxisLabel(xAxis, xAxisOptionGroups)),
        [xAxis, xAxisConfig, xAxisOptionGroups],
    );

    const xAxisKpiControlsMeta = useMemo(() => buildKpiAxisControlsMeta(xAxisConfig), [xAxisConfig]);

    const timePeriod = useMemo(
        () =>
            hasValidTimePeriod
                ? { start_timestamp: startTimestamp, end_timestamp: endTimestamp }
                : null,
        [endTimestamp, hasValidTimePeriod, startTimestamp],
    );

    const requestFilters = useMemo(
        () => ({
            dim1_id_list: [...dim1_id_list],
            dim2_id_list: [...dim2_id_list],
        }),
        [dim1_id_list, dim2_id_list],
    );

    const canFetchChart = isInitialized && hasValidTimePeriod && !filtersLoading && hasResolvedFilterIds;

    const chartRequest = useMemo(
        () =>
            canFetchChart && timePeriod
                ? buildPpaChartRequest({
                      xAxis,
                      yAxis,
                      bubbleRepresents,
                      bubbleSize,
                      xAxisConfig,
                      yAxisConfig,
                      bubbleRepresentsConfig,
                      bubbleSizeConfig,
                      timePeriod,
                      filters: requestFilters,
                      dimensionAxisConfig: xAxisDimensionConfig,
                      kpiAxisConfig: xAxisKpiConfig,
                      xAxisGroupByControlsMeta,
                      tableKpiIds: allTableKpiIds,
                  })
                : null,
        [
            canFetchChart,
            timePeriod,
            xAxis,
            yAxis,
            bubbleRepresents,
            bubbleSize,
            xAxisConfig,
            yAxisConfig,
            bubbleRepresentsConfig,
            bubbleSizeConfig,
            xAxisDimensionConfig,
            xAxisKpiConfig,
            xAxisGroupByControlsMeta,
            allTableKpiIds,
            requestFilters,
        ],
    );

    const tableRequest = useMemo(
        () =>
            chartRequest && tableSearchKeyword.trim()
                ? appendTableSearchToRequest(chartRequest, tableSearchKeyword)
                : chartRequest,
        [chartRequest, tableSearchKeyword],
    );

    // Mirror the rest of the app: the empty-selection panel is driven by the event-driven
    // `filterNullError` flag (set only when an applied filter resolves to nothing and cleared while a
    // fetch is in flight), instead of re-deriving it from loading flags. This avoids the transient
    // flash during global-filter changes, where loading briefly flips false before ids are populated.
    const showNoFilterSelection = isInitialized && !filtersLoading && Boolean(filterNullError);

    const isChartBootstrapLoading =
        isMetaLoading ||
        !isInitialized ||
        filtersLoading ||
        !hasValidTimePeriod ||
        (!hasResolvedFilterIds && !showNoFilterSelection);

    useEffect(() => {
        setXAxisDimensionConfig(undefined);
        setXAxisKpiConfig(undefined);
    }, [xAxis]);

    return {
        title,
        tooltip,
        xAxis,
        setXAxis,
        yAxis,
        setYAxis,
        bubbleRepresents,
        setBubbleRepresents,
        bubbleSize,
        setBubbleSize,
        showHeatMap,
        setShowHeatMap,
        xAxisConfig,
        yAxisConfig,
        bubbleRepresentsConfig,
        bubbleSizeConfig,
        heatMapToggle,
        tableKpiOptionItems,
        tableTooltip,
        selectedKpis,
        toggleTableKpi,
        tableSearchInput,
        setTableSearchInput,
        submitTableSearch,
        tableSearchKeyword,
        xAxisOptionGroups,
        yAxisOptionGroups,
        bubbleRepresentsOptionGroups,
        bubbleSizeOptionGroups,
        xAxisGroupByControlsMeta,
        xAxisKpiControlsMeta,
        isXAxisKpi,
        xAxisDimensionConfig,
        setXAxisDimensionConfig,
        xAxisKpiConfig,
        setXAxisKpiConfig,
        chartRequest,
        tableRequest,
        globalFilterQueryKey,
        isChartBootstrapLoading,
        showNoFilterSelection,
    };
}
